import {createContext, createElement, type ReactNode, type RefObject, useContext, useLayoutEffect} from 'react';

type TipsListScrollRefs = {
    viewportRef: RefObject<HTMLDivElement | null>;
    scrollTopRef: RefObject<number>;
};

const TipsListScrollContext = createContext<TipsListScrollRefs | null>(null);

type TipsListScrollProviderProps = TipsListScrollRefs & {
    children: ReactNode;
};

export const TipsListScrollProvider = ({
    children,
    viewportRef,
    scrollTopRef,
}: TipsListScrollProviderProps) =>
    createElement(TipsListScrollContext.Provider, {value: {viewportRef, scrollTopRef}}, children);

export const useTipsListScrollRefs = () => {
    const scrollRefs = useContext(TipsListScrollContext);
    if (!scrollRefs) {
        throw new Error('useTipsListScrollRefs must be used within TipsListScrollProvider');
    }

    return scrollRefs;
};

export const useTipsListScrollRestore = (
    viewportRef: RefObject<HTMLDivElement | null>,
    scrollTopRef: RefObject<number>
) => {
    // These provider-owned refs stay stable while the view is mounted.
    // The layout effect runs again when the list view remounts after navigation.
    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        viewport.scrollTop = scrollTopRef.current;
    }, [scrollTopRef, viewportRef]);
};

export const saveTipsListScrollTop = (
    viewportRef: RefObject<HTMLDivElement | null>,
    scrollTopRef: RefObject<number>
) => {
    scrollTopRef.current = viewportRef.current?.scrollTop ?? 0;
};
